import type { EventId, Player, PlayerId, SessionEvent } from '@/schemas';
import {
  PlayerSchema,
  PlayerId as PlayerIdSchema,
  EventId as EventIdSchema,
  createPlayerId,
  type UserId,
} from '@/schemas';
import { db } from './client';
import { addPlayerEvent, updatePlayerCacheFromEvents } from './player-events';
import { addSessionEvent, getSessionEvents } from './session-events';

/**
 * Normalize player name by trimming whitespace and normalizing internal whitespace
 */
export const normalizePlayerName = (name: string): string => {
  // eslint-disable-next-line unicorn/prefer-string-replace-all -- replaceAll doesn't support regex
  return name.trim().replace(/\s+/g, ' ');
};

export type ListPlayersOptions = {
  includeDeleted?: boolean;
};

/**
 * Get players from cache table
 */
export const listPlayers = async (options: ListPlayersOptions = {}): Promise<Player[]> => {
  const { includeDeleted = false } = options;
  const collection = includeDeleted ? db.players : db.players.filter((player) => !player.deletedAt);

  const players = await collection.toArray();
  return players.map((player) => PlayerSchema.parse(player));
};

/**
 * Get single player by ID
 */
export const getPlayer = async (playerId: PlayerId): Promise<Player | undefined> => {
  const id = PlayerIdSchema.parse(playerId);
  const player = await db.players.get(id);
  if (!player) return undefined;
  return PlayerSchema.parse(player);
};

/**
 * Validate that a player exists and is not deleted
 *
 * @param playerId - The player ID to validate
 * @throws Error if player doesn't exist or is deleted
 */
export async function validatePlayerExists(playerId: PlayerId): Promise<void> {
  const id = PlayerIdSchema.parse(playerId);
  const player = await db.players.get(id);
  if (!player) {
    throw new Error(`Player ${playerId} does not exist`);
  }
  const parsedPlayer = PlayerSchema.parse(player);
  if (parsedPlayer.deletedAt) {
    throw new Error(`Player ${playerId} has been deleted`);
  }
}

/**
 * Validate that multiple players exist and are not deleted
 *
 * @param playerIds - Array of player IDs to validate
 * @throws Error if any player doesn't exist or is deleted
 */
export async function validatePlayersExist(playerIds: PlayerId[]): Promise<void> {
  const errors: string[] = [];
  const validationPromises = playerIds.map(async (playerId) => {
    try {
      await validatePlayerExists(playerId);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }
  });

  await Promise.all(validationPromises);

  if (errors.length > 0) {
    throw new Error(`Player validation failed: ${errors.join('; ')}`);
  }
}

/**
 * Search players by name or nickname (case-insensitive substring match)
 */
export const searchPlayers = async (query: string): Promise<Player[]> => {
  if (!query.trim()) {
    return listPlayers();
  }

  const normalizedQuery = query.toLowerCase().trim();
  const allPlayers = await listPlayers();

  return allPlayers.filter((player) => {
    const nameMatch = player.name.toLowerCase().includes(normalizedQuery);
    const nicknameMatch = player.nickname?.toLowerCase().includes(normalizedQuery);
    return nameMatch || nicknameMatch;
  });
};

/**
 * Create a new player
 */
export type CreatePlayerInput = {
  name: string;
  nickname?: string;
  handle?: string;
  color?: string;
  userId?: UserId;
};

export const createPlayer = async (input: CreatePlayerInput): Promise<PlayerId> => {
  const normalizedName = normalizePlayerName(input.name);
  if (!normalizedName) {
    throw new Error('Player name is required');
  }

  const playerId = createPlayerId();

  await addPlayerEvent({
    type: 'PlayerCreated',
    userId: input.userId,
    payload: {
      playerId,
      name: normalizedName,
      nickname: input.nickname ? normalizePlayerName(input.nickname) : undefined,
      handle: input.handle,
      color: input.color,
    },
  });

  return playerId;
};

/**
 * Update an existing player
 */
export type UpdatePlayerInput = {
  name?: string;
  nickname?: string;
  handle?: string;
  color?: string;
  userId?: UserId;
};

export const updatePlayer = async (playerId: PlayerId, input: UpdatePlayerInput): Promise<void> => {
  const id = PlayerIdSchema.parse(playerId);

  const payload: {
    playerId: PlayerId;
    name?: string;
    nickname?: string;
    handle?: string;
    color?: string;
  } = {
    playerId: id,
  };

  if (input.name !== undefined) {
    const normalizedName = normalizePlayerName(input.name);
    if (!normalizedName) {
      throw new Error('Player name cannot be empty');
    }
    payload.name = normalizedName;
  }

  if (input.nickname !== undefined) {
    payload.nickname = input.nickname ? normalizePlayerName(input.nickname) : undefined;
  }

  if (input.handle !== undefined) {
    payload.handle = input.handle || undefined; // Convert empty string to undefined
  }

  if (input.color !== undefined) {
    payload.color = input.color;
  }

  await addPlayerEvent({
    type: 'PlayerUpdated',
    userId: input.userId,
    payload,
  });
};

/**
 * Delete a player (soft delete)
 */
export const deletePlayer = async (playerId: PlayerId): Promise<void> => {
  const id = PlayerIdSchema.parse(playerId);

  await addPlayerEvent({
    type: 'PlayerDeleted',
    userId: undefined,
    payload: {
      playerId: id,
    },
  });
};

/**
 * Restore a deleted player
 */
export const restorePlayer = async (playerId: PlayerId): Promise<void> => {
  const id = PlayerIdSchema.parse(playerId);

  await addPlayerEvent({
    type: 'PlayerRestored',
    userId: undefined,
    payload: {
      playerId: id,
    },
  });
};

/**
 * Claim a player (associate with current user)
 * Optionally sets a handle during claim
 */
export const claimPlayer = async (
  playerId: PlayerId,
  userId: UserId,
  handle?: string,
): Promise<void> => {
  const id = PlayerIdSchema.parse(playerId);

  // Normalize handle (trim and convert empty to undefined)
  const handleValue = handle?.trim() || undefined;

  await addPlayerEvent({
    type: 'PlayerClaimed',
    userId,
    payload: {
      playerId: id,
      handle: handleValue, // Optional handle set during claim
    },
  });
};

/**
 * Unclaim a player (disassociate from user)
 * Releases handle if player has one
 */
export const unclaimPlayer = async (playerId: PlayerId): Promise<void> => {
  const id = PlayerIdSchema.parse(playerId);

  // Get current player to check for handle
  const player = await getPlayer(id);
  const userId = player?.userId;

  // Release handle if player has one
  if (player?.handle) {
    const { releaseHandle } = await import('@/lib/supabase/handles');
    const { recordHandleRelease } = await import('@/lib/supabase/handle-releases');
    try {
      await releaseHandle(player.handle);
      // Record release for 30-day reclaim period
      recordHandleRelease(player.handle, id, userId);
    } catch (error) {
      // Log error but continue with unclaim
      // Handle will be cleared in event handler anyway
      console.warn('Failed to release handle during unclaim:', error);
    }
  }

  await addPlayerEvent({
    type: 'PlayerUnclaimed',
    userId: undefined,
    payload: {
      playerId: id,
    },
  });
};

const sessionEventContainsPlayerId = (event: SessionEvent, playerId: PlayerId): boolean => {
  return JSON.stringify(event).includes(String(playerId));
};

/**
 * Merge two players - combines source player into target player
 * Updates all session references and soft deletes source player
 */
export const mergePlayers = async (
  sourcePlayerId: PlayerId,
  targetPlayerId: PlayerId,
): Promise<EventId> => {
  const sourceId = PlayerIdSchema.parse(sourcePlayerId);
  const targetId = PlayerIdSchema.parse(targetPlayerId);

  if (sourceId === targetId) {
    throw new Error('Cannot merge a player with itself');
  }

  const mergeEventId = await db.transaction(
    'rw',
    db.playerEvents,
    db.players,
    db.sessionEvents,
    db.sessionMetadata,
    async () => {
      const sourcePlayer = await getPlayer(sourceId);
      const targetPlayer = await getPlayer(targetId);

      if (!sourcePlayer || sourcePlayer.deletedAt) {
        throw new Error('Source player not found');
      }

      if (!targetPlayer || targetPlayer.deletedAt) {
        throw new Error('Target player not found');
      }

      // Release handle if source player has one
      if (sourcePlayer.handle) {
        const { releaseHandle } = await import('@/lib/supabase/handles');
        const { recordHandleRelease } = await import('@/lib/supabase/handle-releases');
        try {
          await releaseHandle(sourcePlayer.handle);
          // Record release for 30-day reclaim period
          recordHandleRelease(sourcePlayer.handle, sourceId, sourcePlayer.userId);
        } catch (error) {
          // Log error but continue with merge
          // Handle will be cleared when source player is deleted
          console.warn('Failed to release handle during merge:', error);
        }
      }

      const createdMergeEventId = await addPlayerEvent({
        type: 'PlayerMerged',
        userId: undefined,
        payload: {
          sourcePlayerId: sourceId,
          targetPlayerId: targetId,
        },
      });

      await addPlayerEvent({
        type: 'PlayerDeleted',
        userId: undefined,
        payload: {
          playerId: sourceId,
        },
      });

      const sessions = await db.sessionMetadata.toArray();
      for (const session of sessions) {
        const events = await getSessionEvents(session.id);
        const containsSource = events.some((event) =>
          sessionEventContainsPlayerId(event, sourceId),
        );

        if (!containsSource) {
          continue;
        }

        await addSessionEvent({
          sessionId: session.id,
          type: 'PlayerIdMapped',
          userId: undefined,
          payload: {
            sourcePlayerId: sourceId,
            targetPlayerId: targetId,
            reason: 'player_merge',
          },
        });
      }
      return createdMergeEventId;
    },
  );
  return mergeEventId;
};

/**
 * Unmerge a previously merged player - restores source player
 * Finds the PlayerMerged event and reverses the merge
 */
export const unmergePlayers = async (mergedEventId: EventId): Promise<void> => {
  await db.transaction(
    'rw',
    db.playerEvents,
    db.players,
    db.sessionEvents,
    db.sessionMetadata,
    async () => {
      const mergeEventId = EventIdSchema.parse(mergedEventId);
      const mergeEvent = await db.playerEvents.get(mergeEventId);
      if (!mergeEvent || mergeEvent.type !== 'PlayerMerged') {
        throw new Error('PlayerMerged event not found');
      }

      const { sourcePlayerId, targetPlayerId } = mergeEvent.payload;

      await addPlayerEvent({
        type: 'PlayerUnmerged',
        userId: mergeEvent.userId,
        payload: {
          playerId: sourcePlayerId,
        },
      });

      const sourcePlayerEvents = await db.playerEvents
        .where('payload.playerId')
        .equals(sourcePlayerId)
        .toArray();

      const deleteEvent = sourcePlayerEvents.find(
        (event) => event.type === 'PlayerDeleted' && event.timestamp > mergeEvent.timestamp,
      );

      if (deleteEvent) {
        await db.playerEvents.delete(deleteEvent.id);
        await updatePlayerCacheFromEvents(sourcePlayerId);
      }

      const sessions = await db.sessionMetadata.toArray();
      for (const session of sessions) {
        const events = await getSessionEvents(session.id);
        const mappingEvents = events.filter(
          (event): event is Extract<SessionEvent, { type: 'PlayerIdMapped' }> =>
            event.type === 'PlayerIdMapped' &&
            event.payload.sourcePlayerId === sourcePlayerId &&
            event.payload.targetPlayerId === targetPlayerId &&
            event.payload.reason === 'player_merge',
        );

        if (mappingEvents.length === 0) {
          continue;
        }

        for (const mappingEvent of mappingEvents) {
          await addSessionEvent({
            sessionId: session.id,
            type: 'PlayerIdUnmapped',
            userId: mergeEvent.userId,
            payload: {
              originalMappingEventId: mappingEvent.id,
              sourcePlayerId,
              targetPlayerId,
            },
          });
        }
      }
    },
  );
};
